"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Plus, User, DollarSign, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { filterEmployeesByPosition } from "../utils/employeeRoleUtils";
import {
  calculateDynamicTax,
  getDynamicTaxRateLabel,
} from "../utils/dynamicTaxCalculator";
import { taxManagementService } from "../apis/taxManagementService";

export function InlineTipAddBar({
  positionName = "Dealer",
  employees = [],
  onAdd,
  isSubmitting = false,
  showOweToToast = false,
}) {
  const [empName, setEmpName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [amount, setAmount] = useState("");
  const [oweToToast, setOweToToast] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [taxSettings, setTaxSettings] = useState([]);
  const [submittingLocal, setSubmittingLocal] = useState(false);

  // Input refs for automatic focus management
  const empInputRef = useRef(null);
  const amountInputRef = useRef(null);
  const oweToToastInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Fetch dynamic tax settings once on mount
  useEffect(() => {
    let isMounted = true;
    taxManagementService
      .getTaxSettings()
      .then((res) => {
        if (isMounted && res.success && Array.isArray(res.data)) {
          setTaxSettings(res.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching tax settings for inline tip bar:", err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Reset all inputs whenever switching between tip positions
  useEffect(() => {
    setEmpName("");
    setSearchTerm("");
    setAmount("");
    setOweToToast("");
    setIsDropdownOpen(false);
    setHighlightedIndex(0);
  }, [positionName]);

  // Filter employees according to position (handles multi-role staff too)
  const positionEmployees = useMemo(() => {
    return filterEmployeesByPosition(employees, positionName);
  }, [employees, positionName]);

  // Helper to find an employee in positionEmployees
  const findMatchingEmployee = useCallback(
    (nameOrTerm) => {
      if (!nameOrTerm || !Array.isArray(positionEmployees) || positionEmployees.length === 0) {
        return null;
      }
      const clean = String(nameOrTerm).trim().toLowerCase();
      if (!clean) return null;

      return (
        positionEmployees.find((emp) => {
          const empNameStr = typeof emp === "string" ? emp : emp?.name || "";
          return empNameStr.trim().toLowerCase() === clean;
        }) || null
      );
    },
    [positionEmployees]
  );

  // Filter matching employees based on current search text (all employees navigatable)
  const filteredEmployees = useMemo(() => {
    if (!positionEmployees || !Array.isArray(positionEmployees)) return [];
    const term = (searchTerm || "").trim().toLowerCase();
    if (!term) return positionEmployees;

    return positionEmployees.filter((emp) => {
      if (typeof emp === "string") {
        return emp.toLowerCase().includes(term);
      } else if (emp && typeof emp === "object" && emp.name) {
        const haystack = `${emp.name} ${emp.role || ""}`.toLowerCase();
        return haystack.includes(term);
      }
      return false;
    });
  }, [positionEmployees, searchTerm]);

  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredEmployees.length]);

  // Auto-scroll dropdown container so highlighted item is always in visible view
  useEffect(() => {
    if (isDropdownOpen && listRef.current) {
      const container = listRef.current;
      const highlightedEl = container.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedEl) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elTop = highlightedEl.offsetTop;
        const elBottom = elTop + highlightedEl.offsetHeight;

        if (elTop < containerTop) {
          container.scrollTop = elTop;
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight;
        }
      }
    }
  }, [highlightedIndex, isDropdownOpen]);

  // Close dropdown and discard any unlisted free text
  const closeDropdownAndSync = useCallback(() => {
    setIsDropdownOpen(false);
    if (searchTerm) {
      const match = findMatchingEmployee(searchTerm);
      if (match) {
        setEmpName(typeof match === "string" ? match : match.name);
      }
    }
    setSearchTerm("");
  }, [searchTerm, findMatchingEmployee]);

  // Close dropdown on outside click or when moving mouse to the sidebar
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        closeDropdownAndSync();
      }
    };

    const handleSidebarHover = () => {
      closeDropdownAndSync();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    const sidebar = document.querySelector("aside");
    if (sidebar) {
      sidebar.addEventListener("mouseenter", handleSidebarHover);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      if (sidebar) {
        sidebar.removeEventListener("mouseenter", handleSidebarHover);
      }
    };
  }, [closeDropdownAndSync]);

  // Dynamic calculations for Tax & Paid Out
  const calculations = useMemo(() => {
    const amt = parseFloat(amount) || 0;
    const taxCalc = calculateDynamicTax(amt, positionName, taxSettings);
    const netPayout = Math.max(0, taxCalc.paidOut);

    return {
      tax: taxCalc.tax,
      ratePercentage: taxCalc.ratePercentage,
      rateLabel: getDynamicTaxRateLabel(positionName, taxSettings),
      netPayout,
    };
  }, [amount, positionName, taxSettings]);

  const selectEmployee = useCallback((emp) => {
    const name = typeof emp === "string" ? emp : emp?.name || "";
    setEmpName(name);
    setSearchTerm("");
    setIsDropdownOpen(false);
    // Smoothly focus Amount input for immediate typing
    setTimeout(() => {
      amountInputRef.current?.focus();
    }, 50);
  }, []);

  // Keyboard navigation for employee dropdown
  const handleEmployeeKeyDown = (e) => {
    if (isDropdownOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedIndex((prev) => (prev + 1) % filteredEmployees.length);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedIndex((prev) =>
            prev <= 0 ? filteredEmployees.length - 1 : prev - 1
          );
        }
        return;
      }
      if (e.key === "PageDown") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedIndex((prev) => Math.min(filteredEmployees.length - 1, prev + 5));
        }
        return;
      }
      if (e.key === "PageUp") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedIndex((prev) => Math.max(0, prev - 5));
        }
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedIndex(0);
        }
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        if (filteredEmployees.length > 0) {
          setHighlightedIndex(filteredEmployees.length - 1);
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filteredEmployees.length > 0) {
          e.preventDefault();
          const selected = filteredEmployees[highlightedIndex] || filteredEmployees[0];
          selectEmployee(selected);
          return;
        } else {
          e.preventDefault();
          toast.error(`Please select a valid ${positionName} from the list`);
          return;
        }
      }
      if (e.key === "Escape") {
        setIsDropdownOpen(false);
        setSearchTerm("");
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const verified = findMatchingEmployee(empName);
      if (verified && amountInputRef.current) {
        amountInputRef.current.focus();
        amountInputRef.current.select?.();
        return;
      }
      toast.error(`Please select a valid ${positionName} from the list`);
      empInputRef.current?.focus();
      setIsDropdownOpen(true);
    }
  };

  const handleAmountKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If Owe To Toast exists for this module (Waitress & Bartender), advance to it
      if (showOweToToast && oweToToastInputRef.current) {
        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
          toast.error("Please enter a valid tip amount");
          amountInputRef.current?.focus();
          return;
        }
        oweToToastInputRef.current.focus();
        oweToToastInputRef.current.select?.();
        return;
      }
      // If there is no owe to toast, proceed directly with adding the record
      handleSubmit();
    }
  };

  const handleOweToToastKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    // Strictly verify employee is in the listed employees for this position
    const verifiedEmp = findMatchingEmployee(empName) || (searchTerm ? findMatchingEmployee(searchTerm) : null);
    if (!verifiedEmp) {
      toast.error(`Please select a valid ${positionName} from the employee list`);
      setEmpName("");
      setSearchTerm("");
      empInputRef.current?.focus();
      setIsDropdownOpen(true);
      return;
    }

    const officialEmpName = typeof verifiedEmp === "string" ? verifiedEmp : verifiedEmp?.name;
    const empRole = typeof verifiedEmp === "object" ? verifiedEmp?.role : null;

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid tip amount");
      amountInputRef.current?.focus();
      return;
    }

    const parsedToast = showOweToToast ? parseFloat(oweToToast) || 0 : 0;

    try {
      setSubmittingLocal(true);
      const payload = {
        empname: officialEmpName,
        amount: parsedAmount,
        position: empRole || positionName,
        ...(showOweToToast ? { owetotoast: parsedToast } : {}),
      };

      if (onAdd) {
        await onAdd(payload);
      }

      // Reset fields after submission
      setEmpName("");
      setSearchTerm("");
      setAmount("");
      setOweToToast("");
      setIsDropdownOpen(false);

      // Re-focus employee input ready for next record
      setTimeout(() => {
        empInputRef.current?.focus();
      }, 80);
    } catch (err) {
      console.error("Error submitting tipout record:", err);
    } finally {
      setSubmittingLocal(false);
    }
  };

  const busy = isSubmitting || submittingLocal;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100">
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <span>Press</span>
          <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-300 text-[11px] font-mono text-gray-700">
            Enter ↵
          </kbd>
          <span>to save</span>
        </p>

        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
          Rate: {calculations.rateLabel}
        </span>
      </div>

      {/* Inline Form Fields */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
        {/* 1. Employee Searchable Dropdown */}
        <div ref={dropdownRef} className="relative flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Employee Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <User size={15} />
            </div>
            <Input
              ref={empInputRef}
              type="text"
              value={isDropdownOpen ? searchTerm : empName}
              placeholder={`Select ${positionName}...`}
              onFocus={() => {
                setSearchTerm(empName);
                setIsDropdownOpen(true);
              }}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                setIsDropdownOpen(true);
                // Only set empName if typed value is an exact match for a listed employee
                const exact = findMatchingEmployee(val);
                if (exact) {
                  setEmpName(typeof exact === "string" ? exact : exact.name);
                } else {
                  setEmpName("");
                }
              }}
              onKeyDown={handleEmployeeKeyDown}
              disabled={busy}
              className="pl-9 pr-8 text-sm"
            />
            {(empName || searchTerm) && (
              <button
                type="button"
                onClick={() => {
                  setEmpName("");
                  setSearchTerm("");
                  empInputRef.current?.focus();
                  setIsDropdownOpen(true);
                }}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Autocomplete Dropdown List */}
          {isDropdownOpen && (
            <div
              ref={listRef}
              className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto py-1"
            >
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((emp, index) => {
                  const name = typeof emp === "string" ? emp : emp?.name || "";
                  const role = typeof emp === "object" ? emp?.role : "";
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <button
                      key={typeof emp === "object" ? emp.id || `${name}-${index}` : `${name}-${index}`}
                      data-index={index}
                      type="button"
                      onClick={() => selectEmployee(emp)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full text-left px-3.5 py-2 text-xs flex items-center justify-between transition-colors ${
                        isHighlighted
                          ? "bg-gray-100 text-gray-900 font-semibold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <User size={13} className="text-gray-400" />
                        <span>{name}</span>
                      </div>
                      {role && (
                        <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                          {role}
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-2.5 text-xs text-gray-500 text-center">
                  No matching {positionName.toLowerCase()}s found
                </div>
              )}
            </div>
          )}
        </div>

        {/* 2. Tip Amount Input */}
        <div className="w-full sm:w-44">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Tip Amount <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <DollarSign size={15} />
            </div>
            <Input
              ref={amountInputRef}
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={handleAmountKeyDown}
              placeholder="0.00"
              disabled={busy}
              className="pl-8 text-sm font-semibold"
            />
          </div>
        </div>

        {/* 3. Owe To Toast Input (Waitress & Bartender only) */}
        {showOweToToast && (
          <div className="w-full sm:w-40">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Owe To Toast
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <DollarSign size={15} />
              </div>
              <Input
                ref={oweToToastInputRef}
                type="number"
                step="0.01"
                min="0"
                value={oweToToast}
                onChange={(e) => setOweToToast(e.target.value)}
                onKeyDown={handleOweToToastKeyDown}
                placeholder="0.00"
                disabled={busy}
                className="pl-8 text-sm"
              />
            </div>
          </div>
        )}

        {/* 4. Live Calculation Badges */}
        <div className="flex items-center gap-2 self-end pb-1 pt-1 sm:pt-0">
          <div className="px-3 py-1.5 rounded-md bg-gray-50 border border-gray-200 text-center">
            <span className="block text-[10px] uppercase font-bold text-gray-500">
              Tax
            </span>
            <span className="text-xs font-bold text-rose-600">
              ${calculations.tax.toFixed(2)}
            </span>
          </div>

          <div className="px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-center">
            <span className="block text-[10px] uppercase font-bold text-emerald-700">
              Net Paid Out
            </span>
            <span className="text-xs font-bold text-emerald-800">
              ${calculations.netPayout.toFixed(2)}
            </span>
          </div>
        </div>

        {/* 5. Add Record Button */}
        <div className="self-end pb-0.5">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !empName || !findMatchingEmployee(empName) || !amount}
            className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black font-semibold shadow-sm px-4"
          >
            {busy ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1.5 h-4 w-4 stroke-[2.5]" />
            )}
            Add Record
          </Button>
        </div>
      </div>
    </div>
  );
}
